/**
 * SessionRecordingContext – globaler Zustand für laufende Sitzungsaufnahmen.
 *
 * Ermöglicht es, eine Aufnahme zu starten und dann in der App frei zu navigieren,
 * während die Aufnahme im Hintergrund weiterläuft. Die FloatingRecordingBar zeigt
 * den Status und Steuerung an.
 *
 * Segmente statt nur Fließtext
 * ----------------------------
 * Die Aufnahme wird ohnehin in 30-Sekunden-Chunks transkribiert. Bisher
 * wurden die Chunk-Grenzen sofort weggeworfen (`transcript + ' ' + text`),
 * womit `/session` nur einen Fließtext hinterließ. `generate-gncp-draft`
 * verlangt aber `transcription_data` als Segmentliste `{ id, text }` und
 * antwortet ohne sie mit `409 no_transcript` — deshalb hat die Seite nie
 * einen GNCP-Entwurf erzeugt, obwohl sie genau das versprach.
 *
 * Die Chunks sind die natürlichen Segmente. Wir behalten sie jetzt, statt
 * sie künstlich zu rekonstruieren: die IDs werden im GNCP-Entwurf zur
 * Belegkette (jeder Abschnitt zitiert die Segmente, aus denen er stammt —
 * `normalizeSections` verwirft Zitate auf unbekannte IDs).
 */

import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { callTranscribe } from '@/services/transcribe';

export type FloatingRecordingState = 'idle' | 'recording' | 'paused' | 'stopping';

/**
 * Ein transkribierter Aufnahme-Chunk. Shape-kompatibel zu `Segment` in
 * `supabase/functions/generate-gncp-draft/index.ts` — bewusst minimal
 * (`id` + `text`), mehr liest die Function nicht.
 */
export type TranscriptSegment = {
  id: string;
  text: string;
};

export type StopRecordingResult = {
  transcript: string;
  duration: number;
  segments: TranscriptSegment[];
};

type Ctx = {
  recordingState: FloatingRecordingState;
  patientId: string | null;
  patientName: string | null;
  duration: number;
  transcript: string;
  /** Transkript-Chunks in Aufnahmereihenfolge — Basis für `transcription_data`. */
  segments: TranscriptSegment[];
  startRecording: (patientId: string | null, patientName: string | null) => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => Promise<StopRecordingResult>;
  discardRecording: () => void;
};

const SessionRecordingCtx = createContext<Ctx | null>(null);

export function SessionRecordingProvider({ children }: { children: ReactNode }) {
  const [recordingState, setRecordingState] = useState<FloatingRecordingState>('idle');
  const [patientId, setPatientId] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  // Fortlaufender Zähler für Segment-IDs. Ein Ref, weil die Chunk-Callbacks
  // asynchron laufen und ein State-Closure den Stand beim Definieren sähe.
  const segmentSeqRef = useRef(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunkTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mirror patientId into a ref so async chunk callbacks read the current
  // value (state closure would otherwise capture the value at definition time).
  const patientIdRef = useRef<string | null>(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (chunkTimerRef.current) { clearInterval(chunkTimerRef.current); chunkTimerRef.current = null; }
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  async function transcribeChunk(blob: Blob) {
    try {
      const pid = patientIdRef.current;
      const data = await callTranscribe({
        audio: blob,
        filename: 'chunk.webm',
        context: 'session',
        language: 'de',
        ...(pid ? { patientId: pid } : {}),
      });
      const text = data?.text?.trim();
      if (text) {
        setTranscript((prev) => prev + (prev ? ' ' : '') + text);
        segmentSeqRef.current += 1;
        setSegments((prev) => [...prev, { id: `seg-${segmentSeqRef.current}`, text }]);
      }
    } catch {
      // non-fatal: chunk transcription errors don't abort the recording
    }
  }

  const startRecording = useCallback(async (pid: string | null, pName: string | null) => {
    if (recordingState !== 'idle') return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';
    const mr = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = mr;

    mr.ondataavailable = (e) => {
      if (e.data.size > 0) transcribeChunk(e.data);
    };

    chunkTimerRef.current = setInterval(() => {
      if (mr.state === 'recording') mr.requestData();
    }, 30_000);

    mr.start();
    patientIdRef.current = pid;
    setPatientId(pid);
    setPatientName(pName);
    setTranscript('');
    setSegments([]);
    segmentSeqRef.current = 0;
    setDuration(0);
    setRecordingState('recording');

    timerRef.current = setInterval(() => setDuration((d) => d + 1), 1_000);
  }, [recordingState]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      clearTimers();
      setRecordingState('paused');
    }
  }, [clearTimers]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingState('recording');
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1_000);
    }
  }, []);

  const stopRecording = useCallback(async (): Promise<StopRecordingResult> => {
    clearTimers();
    setRecordingState('stopping');

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());

    // Wait a tick for last chunk to process
    await new Promise((r) => setTimeout(r, 2_000));

    const result = { transcript, duration, segments };
    setRecordingState('idle');
    patientIdRef.current = null;
    setPatientId(null);
    setPatientName(null);
    setTranscript('');
    setSegments([]);
    segmentSeqRef.current = 0;
    setDuration(0);
    return result;
  }, [transcript, duration, segments, clearTimers]);

  const discardRecording = useCallback(() => {
    clearTimers();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setRecordingState('idle');
    patientIdRef.current = null;
    setPatientId(null);
    setPatientName(null);
    setTranscript('');
    setSegments([]);
    segmentSeqRef.current = 0;
    setDuration(0);
  }, [clearTimers]);

  return (
    <SessionRecordingCtx.Provider
      value={{ recordingState, patientId, patientName, duration, transcript, segments, startRecording, pauseRecording, resumeRecording, stopRecording, discardRecording }}
    >
      {children}
    </SessionRecordingCtx.Provider>
  );
}

export function useSessionRecording(): Ctx {
  const ctx = useContext(SessionRecordingCtx);
  if (!ctx) throw new Error('useSessionRecording must be used inside <SessionRecordingProvider>');
  return ctx;
}
